import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, Button, TextField, IconButton,
  InputAdornment, Chip, Tooltip, Skeleton, Pagination,
  Select, MenuItem, FormControl, InputLabel, Collapse,
  Divider, Badge
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { soldBikeService } from "../service/SoldBikeService";
import { invoiceService } from "../service/InvoiceService";
import InvoicePopup from "./InvoicePopup";

/* ─── helpers ─────────────────────────────────── */
const EMPTY_FILTERS = {
  model: "", location: "", customerName: "", mobile: "",
  saleType: "", paymentStatus: "", engineNumber: "",
  chasisNumber: "",
  fromDate: "", toDate: "", month: "", year: "", search: "",
};

const cleanParams = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== ""));

const activeFilterCount = (f) =>
  Object.entries(f).filter(([k, v]) => v !== "" && k !== "search").length;

// ✅ ADDED: latest sale first — falls back to createdAt if saleDate is missing
const sortLatestFirst = (arr) =>
  [...arr].sort((a, b) => {
    const da = new Date(a.saleDate || a.createdAt || 0).getTime();
    const db = new Date(b.saleDate || b.createdAt || 0).getTime();
    return db - da;
  });

const PAGE_SIZE = 10;

/* ─── component ───────────────────────────────── */
const InvoiceList = () => {
  // ✅ CHANGED: `data` is now the FULL filtered result set from the API
  // (the API returns everything matching the filter, unpaginated).
  const [allData, setAllData]     = useState([]);
  const [loading, setLoading]     = useState(false);

  // ✅ ADDED: page is now purely a client-side slicing concern
  const [page, setPage]           = useState(1);

  const [filters, setFilters]     = useState(EMPTY_FILTERS);
  const [applied, setApplied]     = useState({});
  const [showFilters, setShowFilters] = useState(true);

  const [open, setOpen]           = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(null); // saleId being loaded

  /* ── fetch (unchanged API call — no page/limit sent, API returns everything) ── */
  const fetchData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await soldBikeService.getFilteredSoldBikes(cleanParams(params));
      setAllData(sortLatestFirst(res.data ?? []));
      console.log("Fetched", res.data?.length ?? 0, "records");
      setPage(1);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── filter handlers ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const handleApply = () => {
    setApplied(filters);
    fetchData(filters);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setApplied({});
    fetchData({});
  };

  // ✅ CHANGED: no refetch — just move the client-side page
  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  /* ── invoice ── */
  const handleInvoice = async (saleId) => {
    setInvoiceLoading(saleId);
    try {
      const res = await invoiceService.getInvoice(saleId);
      setInvoiceData(res);
      setOpen(true);
    } catch (err) {
      console.error("Invoice error:", err);
    } finally {
      setInvoiceLoading(null);
    }
  };

  const activeCount = activeFilterCount(applied);

  // ✅ ADDED: derived pagination + the actual rows for the current page
  const total      = allData.length;
  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const pageData   = useMemo(
    () => allData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [allData, page]
  );

  /* ── render ── */
  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2,
            bgcolor: "primary.main", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <TwoWheelerIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>Invoice</Typography>
            <Typography variant="caption" color="text.secondary">
              {total} total records · Showroom-wise view
            </Typography>
          </Box>
        </Box>

        <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
          <Badge badgeContent={activeCount} color="primary" invisible={activeCount === 0}>
            <Button
              variant={showFilters ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters((s) => !s)}
              endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              size="small"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </Badge>
        </Tooltip>
      </Box>

      {/* ── Filter Panel ── */}
      <Collapse in={showFilters}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          {/* Row 1 */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
            <TextField size="small" label="Model" name="model"
              value={filters.model} onChange={handleChange} sx={{ minWidth: 140, flex: 1 }} />

            <TextField size="small" label="Location" name="location"
              value={filters.location} onChange={handleChange} sx={{ minWidth: 130, flex: 1 }} />

            <TextField size="small" label="Customer" name="customerName"
              value={filters.customerName} onChange={handleChange} sx={{ minWidth: 150, flex: 1 }} />

            <TextField size="small" label="Mobile" name="mobile"
              value={filters.mobile} onChange={handleChange} sx={{ minWidth: 130, flex: 1 }} />

            <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
              <InputLabel>Sale Type</InputLabel>
              <Select name="saleType" value={filters.saleType} label="Sale Type" onChange={handleChange}>
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="FINANCE">Finance</MenuItem>
                <MenuItem value="EXCHANGE">Exchange</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
              <InputLabel>Due Status</InputLabel>
              <Select name="paymentStatus" value={filters.paymentStatus} label="Due Status" onChange={handleChange}>
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
                <MenuItem value="DUE">Due</MenuItem>
              </Select>
            </FormControl>

            <TextField size="small" label="Engine No" name="engineNumber"
              value={filters.engineNumber} onChange={handleChange} sx={{ minWidth: 150, flex: 1 }} />
          </Box>

          {/* Row 2 */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
            <TextField size="small" label="Chasis No" name="chasisNumber"
              value={filters.chasisNumber} onChange={handleChange} sx={{ minWidth: 150, flex: 1 }} />

            <TextField size="small" label="From Date" name="fromDate" type="date"
              value={filters.fromDate} onChange={handleChange}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, flex: 1 }} />

            <TextField size="small" label="To Date" name="toDate" type="date"
              value={filters.toDate} onChange={handleChange}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, flex: 1 }} />

            <TextField size="small" label="Month" name="month" type="number"
              inputProps={{ min: 1, max: 12 }}
              value={filters.month} onChange={handleChange} sx={{ minWidth: 90, flex: "0 0 90px" }} />

            <TextField size="small" label="Year" name="year" type="number"
              value={filters.year} onChange={handleChange} sx={{ minWidth: 100, flex: "0 0 100px" }} />
          </Box>

          {/* Row 3: Search + Buttons */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small" label="Search" name="search"
              value={filters.search} onChange={handleChange}
              placeholder="Engine / Chassis / Customer / Mobile…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260, flex: 2 }}
            />

            <Button variant="contained" onClick={handleApply} sx={{ px: 3, height: 40 }}>
              Apply Filter
            </Button>
            <Button
              variant="outlined" startIcon={<RestartAltIcon />}
              onClick={handleReset} sx={{ height: 40 }}
            >
              Reset
            </Button>

            {activeCount > 0 && (
              <Chip
                size="small"
                label={`${activeCount} filter${activeCount > 1 ? "s" : ""} active`}
                color="primary" variant="outlined"
                onDelete={handleReset}
              />
            )}
          </Box>
        </Paper>
      </Collapse>

      {/* ── Table ── */}
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Box sx={{ overflowX: "auto", borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 900 }}>

            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                {["#", "Model", "Engine No", "Chassis No", "Customer", "Mobile", "Location", "ExShowroom Price", "Sale Date", "Invoice"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap", py: 1.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {/* ✅ FIXED: was 11 cells against a 10-column header */}
                    {Array.from({ length: 10 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width="80%" /></TableCell>
                    ))}
                  </TableRow>
                ))
                : pageData.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        <ReceiptLongIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3, display: "block", mx: "auto" }} />
                        No records found
                      </TableCell>
                    </TableRow>
                  )
                  : pageData.map((b, idx) => (
                    <TableRow key={b._id} hover>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                        {(page - 1) * PAGE_SIZE + idx + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{b.model || "—"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{b.engineNumber || "—"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{b.chasisNumber || "—"}</TableCell>
                      <TableCell>{b.customerName || "—"}</TableCell>
                      <TableCell>{b.mobile?.[0] || "—"}</TableCell>
                      <TableCell>{b?.location}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {b.salePrice ? `₹ ${Number(b.salePrice).toLocaleString("en-IN")}` : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        {b.saleDate ? new Date(b.saleDate).toLocaleDateString("en-IN") : "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained" size="small"
                          startIcon={<ReceiptLongIcon fontSize="small" />}
                          loading={invoiceLoading === (b.saleId ?? b._id)}
                          onClick={() => handleInvoice(b.saleId ?? b._id)}
                          sx={{ fontSize: 12, px: 1.5, whiteSpace: "nowrap" }}
                        >
                          Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
        </Box>

        {/* ── Pagination (now purely client-side) ── */}
        {totalPages > 1 && (
          <>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {(page - 1) * PAGE_SIZE + 1}–
                {Math.min(page * PAGE_SIZE, total)} of {total}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                size="small" color="primary"
              />
            </Box>
          </>
        )}
      </Paper>

      {/* ── Invoice Popup ── */}
      <InvoicePopup
        open={open}
        onClose={() => { setOpen(false); setInvoiceData(null); }}
        data={invoiceData}
      />
    </Box>
  );
};

export default InvoiceList;